import type { Observer } from "@babylonjs/core/Misc/observable";

import type { Harvest } from "@/game/Harvest";

export interface HarvestGaugeOptions {
  /** Élément hôte du widget. `document.body` par défaut. */
  readonly parent?: HTMLElement;
  /**
   * Objectif de la jauge, en arbres : c'est lui que le remplissage rapporte.
   *
   * La forêt étant sans fin, la récolte n'a pas de plafond naturel ; il en faut
   * pourtant un pour qu'une barre veuille dire quelque chose. Dix arbres — une
   * poignée de minutes de jeu — donnent une progression visible dès le premier
   * coup de hache sans que la jauge sature aussitôt.
   */
  readonly goal?: number;
  /** Intitulé affiché, et nom accessible du widget. */
  readonly label?: string;
}

const DEFAULT_GOAL = 10;
const DEFAULT_LABEL = "Arbres coupés";

/**
 * Jauge de récolte, affichée en haut à gauche de l'écran.
 *
 * Elle ne connaît ni la scène ni les arbres : elle observe un `Harvest` et se
 * redessine quand il change, exactement comme le joystick publie dans
 * l'`InputManager` sans savoir ce que ses directions déclenchent. C'est ce qui
 * permet à la scène de compter ses arbres sans jamais toucher au DOM.
 *
 * Le total continue de monter au-delà de l'objectif — un arbre coupé est un
 * arbre coupé — mais le remplissage, lui, s'arrête à ras bord et la jauge passe
 * dans son état « objectif atteint ».
 */
export class HarvestGauge {
  /** Racine du widget, montée dans le DOM à la construction. */
  readonly element: HTMLElement;

  private readonly harvest: Harvest;
  private readonly goal: number;
  private readonly count: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly observer: Observer<number> | null;

  constructor(harvest: Harvest, options: HarvestGaugeOptions = {}) {
    this.harvest = harvest;
    // Un objectif nul ou négatif ferait diviser par zéro : la valeur par défaut
    // reprend la main plutôt que de laisser une jauge illisible.
    this.goal = options.goal !== undefined && options.goal > 0 ? options.goal : DEFAULT_GOAL;
    const label = options.label ?? DEFAULT_LABEL;

    this.count = document.createElement("strong");
    this.count.className = "gauge__count";

    const value = document.createElement("span");
    value.className = "gauge__value";
    value.append(this.count, ` / ${String(this.goal)}`);

    const name = document.createElement("span");
    name.className = "gauge__label";
    name.textContent = label;

    const head = document.createElement("div");
    head.className = "gauge__head";
    head.append(name, value);

    this.fill = document.createElement("div");
    this.fill.className = "gauge__fill";

    const track = document.createElement("div");
    track.className = "gauge__track";
    track.append(this.fill);

    this.element = document.createElement("div");
    this.element.className = "gauge";
    // `meter` décrit exactement cet objet : une mesure bornée, qu'on lit sans
    // pouvoir agir dessus. Le placement, lui, reste affaire de CSS.
    this.element.setAttribute("role", "meter");
    this.element.setAttribute("aria-label", label);
    this.element.setAttribute("aria-valuemin", "0");
    this.element.setAttribute("aria-valuemax", String(this.goal));
    this.element.append(head, track);

    (options.parent ?? document.body).append(this.element);

    // Le widget peut naître après le premier arbre — changement de scène, HUD
    // reconstruit : il part de l'état courant plutôt que de zéro.
    this.render(harvest.count);
    this.observer = harvest.onChanged.add((total) => {
      this.render(total);
    });
  }

  dispose(): void {
    this.harvest.onChanged.remove(this.observer);
    this.element.remove();
  }

  private render(total: number): void {
    const ratio = Math.min(total / this.goal, 1);

    this.count.textContent = String(total);
    // `scale` plutôt que `width` : la barre est redessinée par le compositeur,
    // sans reflow. Le coin arrondi vient du rail, qui la découpe — mis sur la
    // barre elle-même, la mise à l'échelle l'écraserait.
    this.fill.style.scale = `${String(Math.round(ratio * 1000) / 1000)} 1`;
    this.element.classList.toggle("gauge--full", total >= this.goal);

    // `aria-valuenow` reste dans les bornes annoncées, sans quoi la valeur n'a
    // plus de sens pour une technologie d'assistance ; le total réel, lui, est
    // porté par le texte de remplacement.
    this.element.setAttribute("aria-valuenow", String(Math.min(total, this.goal)));
    const plural = total > 1 ? "s" : "";
    this.element.setAttribute(
      "aria-valuetext",
      `${String(total)} arbre${plural} coupé${plural} (objectif : ${String(this.goal)})`,
    );
  }
}
